import { useEffect, useRef, useState } from "react";
import {
  FiMail,
  FiPhone,
  FiGlobe,
  FiInstagram,
  FiLinkedin,
  FiAlertCircle,
  FiFileText,
  FiUploadCloud,
  FiCheckCircle,
  FiClock,
  FiXCircle,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import { useCompany } from "../hooks/useCompany";
import { statusLabel, statusTone } from "../utils/format";
import TipTapEditor from "../components/TipTapEditor";
import ToastStack from "../components/ToastStack";

const initialForm = {
  nomeFantasia: "",
  razaoSocial: "",
  cnpj: "",
  inscricaoEstadual: "",
  enderecoRua: "",
  enderecoNumero: "",
  enderecoBairro: "",
  enderecoCidade: "",
  enderecoEstado: "",
  enderecoCep: "",
  enderecoComplemento: "",
  tipoEmpresa: "INDUSTRIA",
  emailCorporativo: "",
  whatsapp: "",
  siteOficial: "",
  instagramUrl: "",
  linkedinUrl: "",
  logoUrl: "",
  bioEmpresa: "",
};

const normalizeForm = (data = {}) => {
  const normalized = { ...initialForm, ...data };
  Object.keys(normalized).forEach((key) => {
    if (normalized[key] === null || normalized[key] === undefined) {
      normalized[key] = "";
    }
  });
  return normalized;
};

const identificationRequiredFields = [
  "razaoSocial",
  "cnpj",
  "enderecoRua",
  "enderecoNumero",
  "enderecoBairro",
  "enderecoCidade",
  "enderecoEstado",
  "enderecoCep",
];

const isIdentificationComplete = (data) =>
  identificationRequiredFields.every((key) => data?.[key]?.trim());

const CompanyPage = () => {
  const { company, setCompany, loading } = useCompany();
  const [form, setForm] = useState(initialForm);
  const [savingSection, setSavingSection] = useState("");
  const [logoFileName, setLogoFileName] = useState("");
  const [activeTab, setActiveTab] = useState("juridica");
  const [docUploads, setDocUploads] = useState({
    contrato: null,
    responsavel: null,
  });
  const [docSending, setDocSending] = useState(false);
  const [docUploading, setDocUploading] = useState({ contrato: false, responsavel: false });
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [toasts, setToasts] = useState([]);
  const lastCepLookupRef = useRef("");
  const [cepLoading, setCepLoading] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [identificationSaved, setIdentificationSaved] = useState(false);

  useEffect(() => {
    if (company) {
      const normalized = normalizeForm(company);
      setForm(normalized);
      setContactSaved(
        Boolean(company.emailCorporativo?.trim() && company.whatsapp?.trim())
      );
      setIdentificationSaved(isIdentificationComplete(normalized));
      if (company.logoUrl) {
        const parts = company.logoUrl.split("/");
        const last = parts[parts.length - 1];
        setLogoFileName(last && last.length < 48 ? last : "");
      } else {
        setLogoFileName("");
      }
    }
  }, [company]);

  const loadCompanyDocuments = async (companyId) => {
    if (!companyId) {
      setCompanyDocuments([]);
      return;
    }
    try {
      const { data } = await axiosInstance.get(
        `/companies/${companyId}/documents`
      );
      setCompanyDocuments(data?.data || []);
    } catch (error) {
      setCompanyDocuments([]);
    }
  };

  useEffect(() => {
    if (company?.id) {
      loadCompanyDocuments(company.id);
    } else {
      setCompanyDocuments([]);
    }
  }, [company?.id]);

  const maskWhatsapp = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits.length ? `(${digits}` : "";
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "emailCorporativo" || name === "whatsapp") {
      setContactSaved(false);
    }
    if (identificationRequiredFields.includes(name)) {
      setIdentificationSaved(false);
    }
    const newValue = name === "whatsapp" ? maskWhatsapp(value) : value;
    setForm((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleCepChange = async (event) => {
    const raw = event.target.value;
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const formatted =
      digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;

    setIdentificationSaved(false);
    setForm((prev) => ({ ...prev, enderecoCep: formatted }));

    if (digits.length !== 8) {
      lastCepLookupRef.current = "";
      return;
    }

    if (digits === lastCepLookupRef.current) {
      return;
    }

    lastCepLookupRef.current = digits;
    setCepLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      if (data?.erro) {
        pushToast("error", "CEP não encontrado. Verifique e tente novamente.");
        return;
      }

      setForm((prev) => ({
        ...prev,
        enderecoRua: data.logradouro || prev.enderecoRua,
        enderecoBairro: data.bairro || prev.enderecoBairro,
        enderecoCidade: data.localidade || prev.enderecoCidade,
        enderecoEstado: data.uf ? data.uf.toUpperCase() : prev.enderecoEstado,
        enderecoComplemento: data.complemento || prev.enderecoComplemento,
      }));
      pushToast("success", "Endereço preenchido pelo CEP.");
    } catch (error) {
      pushToast("error", "Não foi possível buscar o CEP agora.");
    } finally {
      setCepLoading(false);
    }
  };

  const handleDocSelect = (key, docType) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!company?.id) {
      pushToast("error", "Salve os dados da empresa antes de enviar documentos.");
      return;
    }

    setDocUploading((prev) => ({ ...prev, [key]: true }));

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", docType);

      await axiosInstance.post(
        `/companies/${company.id}/documents/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setDocUploads((prev) => ({ ...prev, [key]: { name: file.name } }));
      await loadCompanyDocuments(company.id);
      pushToast("success", "Documento enviado com sucesso.");
    } catch (error) {
      pushToast("error", "Não foi possível enviar o documento. Tente novamente.");
    } finally {
      setDocUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleDocsSubmit = async () => {
    pushToast("info", "Os documentos são enviados automaticamente ao selecionar o arquivo.");
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const pushToast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, type, message, title }]);
    window.setTimeout(() => removeToast(id), 3600);
  };

  const handleSectionSave = async (section) => {
    if (!section || section === "documentos") return;

    const sectionFields = {
      juridica: [
        "nomeFantasia",
        "razaoSocial",
        "cnpj",
        "inscricaoEstadual",
        "enderecoRua",
        "enderecoNumero",
        "enderecoBairro",
        "enderecoCidade",
        "enderecoEstado",
        "enderecoCep",
        "enderecoComplemento",
        "tipoEmpresa",
      ],
      contato: [
        "emailCorporativo",
        "whatsapp",
        "siteOficial",
        "instagramUrl",
        "linkedinUrl",
      ],
      marca: ["logoUrl", "bioEmpresa"],
    };

    setSavingSection(section);

    try {
      const payload = (sectionFields[section] || []).reduce((acc, key) => {
        acc[key] = form[key];
        return acc;
      }, {});

      let response;
      if (company?.id) {
        response = await axiosInstance.put(`/companies/${company.id}`, payload);
      } else {
        response = await axiosInstance.post("/companies", payload);
      }

      const updated = response?.data?.data;
      if (updated) {
        setCompany(updated);
        const normalized = normalizeForm(updated);
        setForm(normalized);
        if (section === "contato") {
          setContactSaved(
            Boolean(
              updated.emailCorporativo?.trim() && updated.whatsapp?.trim()
            )
          );
        }
        if (section === "juridica") {
          setIdentificationSaved(isIdentificationComplete(normalized));
        }
      }
      pushToast("success", "Seção salva com sucesso.");
    } catch (error) {
      pushToast("error", "Não foi possível salvar esta seção.");
    } finally {
      setSavingSection("");
    }
  };

  const hasContrato =
    companyDocuments.some((doc) => doc.type === "CONTRATO_SOCIAL") ||
    Boolean(docUploads.contrato?.dataUrl);
  const hasResponsavel =
    companyDocuments.some((doc) => doc.type === "CNH_FRENTE") ||
    Boolean(docUploads.responsavel?.dataUrl);
  const missingDocs = !(hasContrato && hasResponsavel);
  const missingContact = !contactSaved;
  const missingIdentification =
    !identificationSaved || !isIdentificationComplete(form);

  return (
    <div className="fade-in page-stack">
      <ToastStack toasts={toasts} onDismiss={removeToast} />
      <div className="page-header">
        <div>
          <h2>Informações da sua empresa</h2>
          <p>
            Complete os dados jurídicos e comerciais para liberar seus anúncios.
          </p>
        </div>
        {company?.statusVerificacao ? (
          <span
            className="badge"
            data-tone={statusTone(company.statusVerificacao)}
          >
            {statusLabel(company.statusVerificacao)}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="card">Carregando dados...</div>
      ) : (
        <form
          className="section-card form-layout"
          onSubmit={(event) => {
            event.preventDefault();
            handleSectionSave(activeTab);
          }}
        >
          <div className="tabs">
            <button
              type="button"
              className={`tab-button ${
                activeTab === "juridica" ? "active" : ""
              }`}
              onClick={() => setActiveTab("juridica")}
            >
              Identificação
              {missingIdentification ? (
                <span className="tab-alert" aria-label="Identificação pendente">
                  <FiAlertCircle />
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={`tab-button ${
                activeTab === "contato" ? "active" : ""
              }`}
              onClick={() => setActiveTab("contato")}
            >
              Contato
              {missingContact ? (
                <span className="tab-alert" aria-label="Contato pendente">
                  <FiAlertCircle />
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === "marca" ? "active" : ""}`}
              onClick={() => setActiveTab("marca")}
            >
              Marca
            </button>
            <button
              type="button"
              className={`tab-button ${
                activeTab === "documentos" ? "active" : ""
              }`}
              onClick={() => setActiveTab("documentos")}
            >
              Documentos
              {missingDocs ? (
                <span className="tab-alert" aria-label="Documentos pendentes">
                  <FiAlertCircle />
                </span>
              ) : null}
            </button>
          </div>

          {activeTab === "juridica" ? (
            <div className="form-section">
              <div>
                <h3 className="card-title">Identificação jurídica</h3>
                <p className="card-subtitle">
                  Dados essenciais para validação fiscal e compliance.
                </p>
              </div>
              <div className="form-grid">
                <div className="field md:col-span-2">
                  <label className="label">
                    Razão social <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="razaoSocial"
                    value={form.razaoSocial}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="field md:col-span-2">
                  <label className="label">Nome fantasia</label>
                  <input
                    className="input"
                    name="nomeFantasia"
                    value={form.nomeFantasia}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field md:col-span-2">
                  <label className="label">
                    CNPJ <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="cnpj"
                    value={form.cnpj}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="field md:col-span-2">
                  <label className="label">Tipo de empresa</label>
                  <select
                    className="select"
                    name="tipoEmpresa"
                    value={form.tipoEmpresa}
                    onChange={handleChange}
                  >
                    <option value="INDUSTRIA">Industria</option>
                    <option value="FORNECEDOR_PRODUTOR">
                      Fornecedor/Produtor
                    </option>
                    <option value="DISTRIBUIDOR">Distribuidor</option>
                  </select>
                </div>
              </div>
              <div>
                <h4 className="card-title">Endereço comercial</h4>
                <p className="card-subtitle">
                  Informe o endereço principal da empresa.
                </p>
              </div>
              <div className="form-grid form-grid--wide">
                <div className="field">
                  <label className="label">
                    CEP <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoCep"
                    value={form.enderecoCep}
                    onChange={handleCepChange}
                    placeholder="00000-000"
                  />
                  <span className="helper">
                    {cepLoading
                      ? "Buscando endereço..."
                      : "Digite o CEP para preencher automaticamente."}
                  </span>
                </div>
                <div className="field md:col-span-2">
                  <label className="label">
                    Rua <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoRua"
                    value={form.enderecoRua}
                    onChange={handleChange}
                  />
                </div>
                <div className="field">
                  <label className="label">
                    Número <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoNumero"
                    value={form.enderecoNumero}
                    onChange={handleChange}
                  />
                </div>
                <div className="field">
                  <label className="label">
                    Bairro <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoBairro"
                    value={form.enderecoBairro}
                    onChange={handleChange}
                  />
                </div>
                <div className="field">
                  <label className="label">
                    Cidade <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoCidade"
                    value={form.enderecoCidade}
                    onChange={handleChange}
                  />
                </div>
                <div className="field">
                  <label className="label">
                    Estado (UF) <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    className="input"
                    name="enderecoEstado"
                    maxLength={2}
                    value={form.enderecoEstado}
                    onChange={handleChange}
                  />
                </div>
                <div className="field">
                  <label className="label">Complemento</label>
                  <input
                    className="input"
                    name="enderecoComplemento"
                    value={form.enderecoComplemento}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => handleSectionSave("juridica")}
                  disabled={savingSection === "juridica"}
                >
                  {savingSection === "juridica"
                    ? "Salvando..."
                    : "Salvar identificação"}
                </button>
              </div>
            </div>
          ) : null}

          {activeTab === "contato" ? (
            <div className="form-section">
              <div>
                <h3 className="card-title">Contato e presença digital</h3>
                <p className="card-subtitle">
                  Onde os compradores vão encontrar sua empresa.
                </p>
              </div>
              <div className="form-grid">
                <div className="field md:col-span-2">
                  <label className="label">
                    E-mail <span className="text-red-500">(*)</span>
                  </label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FiMail />
                    </span>
                    <input
                      className="input"
                      name="emailCorporativo"
                      type="email"
                      value={form.emailCorporativo}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="field md:col-span-2">
                  <label className="label">
                    WhatsApp <span className="text-red-500">(*)</span>
                  </label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FiPhone />
                    </span>
                    <input
                      className="input"
                      name="whatsapp"
                      value={form.whatsapp}
                      onChange={handleChange}
                      placeholder="(00) 00000-0000"
                      inputMode="numeric"
                      required
                    />
                  </div>
                </div>
                <div className="field md:col-span-2">
                  <label className="label">Site oficial</label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FiGlobe />
                    </span>
                    <input
                      className="input"
                      name="siteOficial"
                      value={form.siteOficial}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="field md:col-span-2">
                  <label className="label">Instagram</label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FiInstagram />
                    </span>
                    <input
                      className="input"
                      name="instagramUrl"
                      value={form.instagramUrl}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="field md:col-span-2">
                  <label className="label">LinkedIn</label>
                  <div className="input-group">
                    <span className="input-icon">
                      <FiLinkedin />
                    </span>
                    <input
                      className="input"
                      name="linkedinUrl"
                      value={form.linkedinUrl}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => handleSectionSave("contato")}
                  disabled={savingSection === "contato"}
                >
                  {savingSection === "contato"
                    ? "Salvando..."
                    : "Salvar contato"}
                </button>
              </div>
            </div>
          ) : null}

          {activeTab === "marca" ? (
            <div className="form-section">
              <div>
                <h3 className="card-title">Marca e apresentação</h3>
                <p className="card-subtitle">
                  Construa uma vitrine profissional para sua marca.
                </p>
              </div>
              <div className="form-grid form-grid--wide">
                <div className="field">
                  <label className="label">Logo da empresa</label>
                  <div className="upload-card">
                    <input
                      className="upload-input"
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          setForm((prev) => ({
                            ...prev,
                            logoUrl: reader.result || "",
                          }));
                          setLogoFileName(file.name);
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    <div className="upload-meta">
                      <span className="upload-title">Enviar arquivo</span>
                      <span className="helper">
                        PNG, JPG ou SVG com fundo claro.
                      </span>
                    </div>
                    {form.logoUrl ? (
                      <div className="upload-preview">
                        <img src={form.logoUrl} alt="Logo preview" />
                        <div className="upload-actions">
                          <span className="upload-filename">
                            {logoFileName || "Logo atual"}
                          </span>
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, logoUrl: "" }));
                              setLogoFileName("");
                            }}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="field">
                <label className="label">Bio da empresa</label>
                <TipTapEditor
                  value={form.bioEmpresa}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, bioEmpresa: value }))
                  }
                  placeholder="Conte sobre a história, diferenciais e capacidade de produção."
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => handleSectionSave("marca")}
                  disabled={savingSection === "marca"}
                >
                  {savingSection === "marca" ? "Salvando..." : "Salvar marca"}
                </button>
              </div>
            </div>
          ) : null}

          {activeTab === "documentos" ? (
            <div className="form-section">
              <div>
                <h3 className="card-title">Documentos obrigatórios</h3>
                <p className="card-subtitle">
                  Anexe o contrato social e o documento do responsável pela
                  empresa. Os arquivos são enviados automaticamente ao
                  selecionar.
                </p>
              </div>

              <div className="form-grid form-grid--wide">
                {[
                  {
                    key: "contrato",
                    docType: "CONTRATO_SOCIAL",
                    label: "Contrato social",
                    hint: "PDF ou imagem legível do contrato social da empresa.",
                  },
                  {
                    key: "responsavel",
                    docType: "CNH_FRENTE",
                    label: "Documento do responsável",
                    hint: "RG ou CNH (frente) do responsável legal.",
                  },
                ].map(({ key, docType, label, hint }) => {
                  const isUploading = docUploading[key];
                  const uploaded = docUploads[key];
                  const existing = companyDocuments
                    .filter((d) => d.type === docType)
                    .sort(
                      (a, b) =>
                        new Date(b.createdAt) - new Date(a.createdAt)
                    );
                  const latest = existing[0];

                  const statusConfig = {
                    APROVADO: {
                      icon: <FiCheckCircle size={13} />,
                      label: "Aprovado",
                      color: "#166534",
                      bg: "#dcfce7",
                    },
                    PENDENTE: {
                      icon: <FiClock size={13} />,
                      label: "Em análise",
                      color: "#92400e",
                      bg: "#fef3c7",
                    },
                    REJEITADO: {
                      icon: <FiXCircle size={13} />,
                      label: "Rejeitado",
                      color: "#991b1b",
                      bg: "#fee2e2",
                    },
                  };

                  const statusInfo = latest
                    ? statusConfig[latest.status]
                    : null;

                  return (
                    <div key={key} className="field">
                      <label className="label">
                        {label} <span style={{ color: "var(--color-danger)" }}>(*)</span>
                      </label>

                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                          padding: "1.25rem",
                          borderRadius: "0.75rem",
                          border: `2px dashed ${
                            latest?.status === "APROVADO"
                              ? "#86efac"
                              : isUploading
                              ? "#93c5fd"
                              : "#cbd5e1"
                          }`,
                          backgroundColor: latest?.status === "APROVADO"
                            ? "#f0fdf4"
                            : isUploading
                            ? "#eff6ff"
                            : "#f8fafc",
                          cursor: isUploading ? "not-allowed" : "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "0.5rem",
                              backgroundColor: isUploading
                                ? "#dbeafe"
                                : latest?.status === "APROVADO"
                                ? "#dcfce7"
                                : "#e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              color: isUploading
                                ? "#2563eb"
                                : latest?.status === "APROVADO"
                                ? "#16a34a"
                                : "#64748b",
                            }}
                          >
                            {isUploading ? (
                              <FiUploadCloud size={20} />
                            ) : (
                              <FiFileText size={20} />
                            )}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                color: "#1f2937",
                                margin: 0,
                              }}
                            >
                              {isUploading
                                ? "Enviando arquivo..."
                                : uploaded
                                ? uploaded.name
                                : "Clique para selecionar"}
                            </p>
                            <p
                              style={{
                                fontSize: "0.78rem",
                                color: "#64748b",
                                margin: "2px 0 0",
                              }}
                            >
                              {hint}
                            </p>
                          </div>

                          {statusInfo && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 10px",
                                borderRadius: "999px",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: statusInfo.color,
                                backgroundColor: statusInfo.bg,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              {statusInfo.icon}
                              {statusInfo.label}
                            </span>
                          )}
                        </div>

                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          style={{ display: "none" }}
                          disabled={isUploading}
                          onChange={handleDocSelect(key, docType)}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </form>
      )}
    </div>
  );
};

export default CompanyPage;
